/**
 * Handles clipboard events from and to the grid and the computer clipboard
 *
 * The reason is because by default the grid copies the valueGetter value, but we want to know both the value and the actual text, in case you are copying the
 * text to the clipboard to paste it somewhere else that is not the grid.
 *
 * So the idea here is that whenever the copy in the grid, we will keep it here as a value, text object and we will copy the actual text from the cell to the computer clipboard
 * so then pasting works as everywhere else in the system. But if you try and paste it into the grid, it will paste the valueGetter value instead.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ClipboardData, ClipboardDataItem } from './clipboardData';
import { watchDebounced } from '@vueuse/core';

export const useClipboardHandler = defineStore('clipboard', () => {
    // A Debunced buffer of all the cells copied before they are added to the clipboard data. This is because the grid fires multiple copy events for each cell when copying a range
    const buffer = ref<ClipboardDataItem[]>([]);

    // Contains the actual clipboard data items. One item euals one paste/copy action
    const clipboardData = ref<ClipboardData | null>(null);

    /**
     * Add a new item to the buffer
     * @param data
     */
    const addToBuffer = (data: ClipboardDataItem) => {
        buffer.value.push(data);
    };

    /**
     * Watch the buffer and add to clipboard data after debounce
     */
    watchDebounced(buffer, () => addBufferToClipboard(), { debounce: 20, deep: true });

    /**
     * This will build one clipboard item from the buffer and add it to the clipboard data
     * @returns
     */
    const addBufferToClipboard = () => {
        if (buffer.value.length === 0) {
            return;
        }
        clipboardData.value = new ClipboardData([...buffer.value]);
        buffer.value = [];
    };

    /**
     * We need to pop the last clipboard data
     * @returns
     */
    const getClipboardData = (systemClipboardData: unknown[][]): ClipboardDataItem[][] => {
        // Now we can pop the last clipboard data
        const data = clipboardData.value;

        const systemClipboardDataHash = systemClipboardData.flatMap((row) => row.map((cell) => `${cell}`)).join('|');

        const returnValue: ClipboardDataItem[][] = [];
        if (!data || data.hash !== systemClipboardDataHash) {
            console.log('System clipboard data does not match our clipboard data, returning system clipboard data');

            // We need to convert the system clipboard data to ClipboardDataItem format. The systemclipboard contains string[][]
            for (let i = 0; i < systemClipboardData.length; i++) {
                const col = systemClipboardData[i];

                // We need to check if the last row has only empty values, if so we skip it. This is an issue from Excel that adds an extra row when copying
                if (i === systemClipboardData.length - 1) {
                    const allEmpty = col.every((cell) => `${cell}`.trim() === '');
                    if (allEmpty) {
                        console.warn('Last row in clipboard data is empty, skipping it', systemClipboardData);
                        break;
                    }
                }

                for (let j = 0; j < col.length; j++) {
                    const value = `${systemClipboardData[i][j]}`;
                    returnValue[i] = returnValue[i] || [];
                    returnValue[i][j] = new ClipboardDataItem(value, value, null);
                }
            }
            return returnValue;
        }

        // We find the number of columns by counting the unique column ids in the items
        const numberOfColumns = new Set(data?.items.map((i) => i.columnId)).size;
        const numberOfRows = data?.items.length! / numberOfColumns;

        // We want to return the data as 2 dimensional array if there are multiple columns
        for (let r = 0; r < numberOfRows; r++) {
            const rowData = [];
            const rowItems = data?.items.slice(r * numberOfColumns, (r + 1) * numberOfColumns) ?? [];
            for (const item of rowItems) {
                rowData.push(item);
            }
            returnValue.push(rowData);
        }

        return returnValue;
    };

    return {
        /**
         * Add item to the buffer
         */
        PushToBuffer: addToBuffer,

        /**
         * Pop the last clipboard data
         */
        GetClipboardData: getClipboardData,
    };
});
